<script>
  import { onMount } from 'svelte';
  import { API_BASE_URL, isAdminUser } from './config.js';
  import Button from './components/common/Button.svelte';
  import ButtonGroup from './components/common/ButtonGroup.svelte';
  import Navigation from './components/layout/Navigation.svelte';
  
  export let setPage;
  
  let user = null; // 사용자 정보
  let isAdmin = false; // 관리자 여부
  let isCompany = false; // 기업 사용자 여부
  let isEditing = true; // 수정 모드
  
  // 상태 변수
  let isProcessing = false;
  let errorMessage = "";
  let isUpdated = false;
  
  // 프로젝트 데이터
  let projectData = {
    id: "",
    title: "",
    company: "",
    skills: [],
    requirements: [],
    preferredSkills: [],
    level: "",
    headcount: "",
    location: "", // 사용자 입력 주소 텍스트 (예: "서울시 강남구 테헤란로 123")
    code_value: "", // 지역 드롭다운에서 선택된 지역 코드 (예: SU001)
    salary: "",
    startDate: "",
    endDate: "",
    deadline: "", // 공고 마감일 추가
    description: "",
    contact: "",
    category: "",
    status: "active", // 기본값: 모집중
    admin_notes: "", // 관리자 비고
    admin_remark: "" // 관리자 비고 2
  };
  
  // 카테고리 선택 옵션
  const categories = [
    "웹 개발",
    "앱 개발",
    "서버/백엔드",
    "프론트엔드",
    "데이터베이스",
    "DevOps",
    "AI/머신러닝",
    "보안",
    "UI/UX",
    "기타"
  ];

  // 상태 옵션 정의
  const statusOptions = [
    { value: "active", label: "모집중", description: "현재 모집중인 공고" },
    { value: "open", label: "채용 시 마감", description: "채용 완료 시 자동으로 마감되는 공고" },
    { value: "closed", label: "상시 채용", description: "상시적으로 인재를 모집하는 공고" },
    { value: "lapse", label: "마감", description: "공고 마감된 상태" }
  ];

  // 지역 선택 관련 상태 변수
  let cities = []; // 시/도 목록
  let selectedCity = ""; // 선택된 시/도 코드
  let isLoadingCities = false; // 시/도 로딩 상태
  let regionError = ""; // 지역 선택 에러 메시지

  // 오늘 날짜를 YYYY-MM-DD 형식으로 반환하는 헬퍼 함수
  function getTodayString() {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  // N일 뒤 날짜를 YYYY-MM-DD 형식으로 반환하는 헬퍼 함수
  function getDateAfterDays(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  }

  // DB에서 가져온 날짜를 YYYY-MM-DD 형식으로 정규화하는 함수
  function normalizeDateFromDB(dateValue) {
    if (!dateValue) return "";
    
    // 이미 YYYY-MM-DD 형식인 경우 (DB에서 ::text로 변환된 경우)
    if (typeof dateValue === 'string') {
      // YYYY-MM-DD 형식인지 확인
      const dateMatch = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        return dateMatch[1]; // 첫 번째 매치 그룹 (YYYY-MM-DD 부분)
      }
    }
    
    // Date 객체이거나 ISO 문자열인 경우 (fallback)
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        console.warn('유효하지 않은 날짜:', dateValue);
        return "";
      }
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.warn('날짜 변환 오류:', dateValue, error);
      return "";
    }
  }



  // 날짜 기본값 설정 함수 (수정: 기존 유효한 날짜는 보존)
  function setDefaultDates() {
    console.log('🗓️ 날짜 기본값 설정 시작:', {
      currentStartDate: projectData.startDate,
      currentEndDate: projectData.endDate,
      currentDeadline: projectData.deadline,
      timestamp: new Date().toISOString()
    });

    // 1. 시작일이 비어있거나 유효하지 않다면 오늘 날짜로 설정
    if (!projectData.startDate || projectData.startDate.trim() === '' || 
        !/^\d{4}-\d{2}-\d{2}$/.test(projectData.startDate)) {
      projectData.startDate = getTodayString();
      console.log('✅ 시작일을 오늘 날짜로 설정:', projectData.startDate);
    }
    
    // 2. 종료일이 비어있거나 유효하지 않다면 시작일보다 5일 뒤로 설정
    if (!projectData.endDate || projectData.endDate.trim() === '' || 
        !/^\d{4}-\d{2}-\d{2}$/.test(projectData.endDate)) {
      const startDate = new Date(projectData.startDate);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 5);
      projectData.endDate = endDate.toISOString().split('T')[0];
      console.log('✅ 종료일을 시작일+5일로 설정:', projectData.endDate);
    }

    // 3. 마감일이 비어있거나 유효하지 않다면 오늘 날짜에서 5일 뒤로 설정
    if (!projectData.deadline || projectData.deadline.trim() === '' || 
        !/^\d{4}-\d{2}-\d{2}$/.test(projectData.deadline)) {
      projectData.deadline = getDateAfterDays(5);
      console.log('✅ 마감일을 오늘+5일로 설정:', projectData.deadline);
    }

    console.log('🗓️ 날짜 기본값 설정 완료:', {
      finalStartDate: projectData.startDate,
      finalEndDate: projectData.endDate,
      finalDeadline: projectData.deadline,
      timestamp: new Date().toISOString()
    });
  }

  // 지역 데이터 로딩 함수들
  
  // 시/도 목록 로딩
  async function loadCities() {
    try {
      isLoadingCities = true;
      regionError = "";
      
      const response = await fetch(`${API_BASE_URL}/api/codes/regions/cities`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        cities = data.data || [];
        console.log('시/도 목록 로딩 완료:', cities.length, '개');
      } else {
        regionError = data.message || "시/도 목록을 불러오는데 실패했습니다.";
      }
    } catch (error) {
      console.error('시/도 목록 로딩 오류:', error);
      regionError = `시/도 목록 로딩 오류: ${error.message}`;
    } finally {
      isLoadingCities = false;
    }
  }
  
  // 시/도 선택 핸들러
  function handleCityChange() {
    console.log('시/도 선택 변경:', selectedCity);
    projectData.code_value = selectedCity; // 지역 코드 저장
  }
  
  // 저장된 지역 코드로부터 시/도 정보를 복원하는 함수
  async function prefillRegionFromCode(locationCode) {
    if (!locationCode) return;
    
    console.log('🔄 지역 코드 프리필 시작:', locationCode);
    
    try {
      // 먼저 시/도 목록이 로딩되어 있는지 확인
      if (cities.length === 0) {
        console.log('📍 시/도 목록 로딩 중...');
        await loadCities();
      }
      
      // 시/도 코드인 경우 또는 구/군 코드에서 시/도 부분만 추출
      if (locationCode.length === 5) {
        if (locationCode.endsWith('000')) {
          // 시/도 코드인 경우
          console.log('🏙️ 시/도 코드 복원:', locationCode);
          selectedCity = locationCode;
          // 시/도만 있는 경우 code_value를 시/도 코드로 설정
          projectData.code_value = locationCode;
        } else {
          // 구/군 코드인 경우 - 해당하는 시/도 코드로 변환
          const cityPrefix = locationCode.substring(0, 2) + '000';
          console.log('🏘️ 구/군 코드에서 시/도 추출:', locationCode, '→ 시/도:', cityPrefix);
          
          const city = cities.find(c => c.code_value === cityPrefix);
          
          if (city) {
            selectedCity = cityPrefix;
            projectData.code_value = cityPrefix;
            console.log('✅ 시/도 선택 완료:', city.code_name);
          } else {
            console.warn('⚠️ 해당 시/도를 찾을 수 없음:', cityPrefix);
          }
        }
      }
    } catch (error) {
      console.error('❌ 지역 정보 복원 오류:', error);
    }
  }

  // 시작일 변경 시 호출되는 함수
  function handleStartDateChange() {
    console.log('📅 시작일 변경됨:', projectData.startDate);
    
    // 종료일이 비어있거나 시작일보다 이전이면 자동으로 5일 뒤로 설정
    if (!projectData.endDate || projectData.endDate.trim() === '' || 
        new Date(projectData.endDate) <= new Date(projectData.startDate)) {
      if (projectData.startDate && projectData.startDate.trim() !== '') {
        const startDate = new Date(projectData.startDate);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 5);
        projectData.endDate = endDate.toISOString().split('T')[0];
        console.log('✅ 종료일 자동 조정:', projectData.endDate);
      }
    }
  }
  
  // 컴포넌트 마운트 시 사용자 정보와 프로젝트 정보 로드
  onMount(async () => {
    // 사용자 정보 로드
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user = JSON.parse(storedUser);
        isAdmin = isAdminUser(user);
        isCompany = user.role === 'company';
      } catch (error) {
        console.error('사용자 정보 파싱 오류:', error);
      }
    }
    
    // localStorage에서 수정할 프로젝트 정보 가져오기
    const editingProjectData = localStorage.getItem('editingProject');
    if (editingProjectData) {
      try {
        const projectToEdit = JSON.parse(editingProjectData);
        
        // 프로젝트 데이터 초기화
        projectData.id = projectToEdit.id || projectToEdit._id || "";
        projectData.title = projectToEdit.title || "";
        projectData.company = projectToEdit.company || "";
        projectData.skills = Array.isArray(projectToEdit.skills) ? [...projectToEdit.skills] : 
                           (typeof projectToEdit.skills === 'string' ? [projectToEdit.skills] : []);
        projectData.requirements = Array.isArray(projectToEdit.requirements) ? [...projectToEdit.requirements] : 
                                 (typeof projectToEdit.requirements === 'string' ? [projectToEdit.requirements] : []);
        projectData.preferredSkills = Array.isArray(projectToEdit.preferredSkills) ? [...projectToEdit.preferredSkills] : 
                                    (Array.isArray(projectToEdit.preferred_skills) ? [...projectToEdit.preferred_skills] : 
                                    (typeof projectToEdit.preferredSkills === 'string' ? [projectToEdit.preferredSkills] : []));
        projectData.level = projectToEdit.level || "";
        projectData.headcount = projectToEdit.headcount || projectToEdit.head_count || "";
        projectData.location = projectToEdit.location || "";
        projectData.code_value = projectToEdit.code_value || projectToEdit.location || "";
        projectData.salary = projectToEdit.salary || "";
        // 날짜 필드 정규화 (DB에서 가져온 날짜를 HTML date input 형식으로 변환)
        const rawStartDate = projectToEdit.startDate || projectToEdit.start_date;
        const rawEndDate = projectToEdit.endDate || projectToEdit.end_date;
        const rawDeadline = projectToEdit.deadline;
        
        projectData.startDate = normalizeDateFromDB(rawStartDate);
        projectData.endDate = normalizeDateFromDB(rawEndDate);
        projectData.deadline = normalizeDateFromDB(rawDeadline);
        
        console.log('📅 DB에서 가져온 날짜 정보:', {
          rawStartDate: rawStartDate,
          rawEndDate: rawEndDate,
          rawDeadline: rawDeadline,
          normalizedStartDate: projectData.startDate,
          normalizedEndDate: projectData.endDate,
          normalizedDeadline: projectData.deadline,
          timestamp: new Date().toISOString()
        });
        
        projectData.description = projectToEdit.description || "";
        projectData.contact = projectToEdit.contact || "";
        projectData.category = projectToEdit.category || "";
        projectData.status = projectToEdit.status || "active";
        projectData.admin_notes = projectToEdit.admin_notes || "";
        projectData.admin_remark = projectToEdit.admin_remark || "";
        
        // 날짜 기본값 설정 (유효한 날짜가 없는 경우에만)
        setDefaultDates();
        
        console.log("불러온 프로젝트 데이터:", projectData);
        
        // 지역 데이터 로딩 및 기존 지역 정보 복원
        await loadCities();
        if (projectData.code_value || projectData.location) {
          await prefillRegionFromCode(projectData.code_value || projectData.location);
        } else {
          // 기존 지역 정보가 없으면 서울특별시(SU000)로 기본 설정
          const seoulCity = cities.find(c => c.code_value === 'SU000');
          if (seoulCity) {
            selectedCity = seoulCity.code_value;
            projectData.code_value = seoulCity.code_value;
            console.log('✅ 기본 지역을 서울특별시로 설정:', seoulCity.code_name, seoulCity.code_value);
          }
        }
        
      } catch (error) {
        console.error('프로젝트 정보 파싱 오류:', error);
        errorMessage = "프로젝트 정보를 불러오는 데 실패했습니다.";
      }
    } else {
      errorMessage = "수정할 프로젝트 정보가 없습니다.";
    }
  });
  
  // 프로젝트 수정 함수
  async function updateProject() {
    try {
      isProcessing = true;
      errorMessage = "";
      
      // 필수 입력값 확인
      if (!projectData.title) {
        errorMessage = "프로젝트 제목을 입력해주세요.";
        isProcessing = false;
        return;
      }
      
      if (!projectData.category) {
        errorMessage = "카테고리를 선택해주세요.";
        isProcessing = false;
        return;
      }

      if (!projectData.status) {
        errorMessage = "공고 상태를 선택해주세요.";
        isProcessing = false;
        return;
      }

      if (!projectData.deadline) {
        errorMessage = "공고 마감일을 입력해주세요.";
        isProcessing = false;
        return;
      }

      // 수정 모드에서는 기존에 저장된 code_value가 있으면 검증 통과
      // 신규 등록 시에는 여전히 필수 검증
      if (!selectedCity) {
        errorMessage = "시/도를 선택해주세요.";
        isProcessing = false;
        return;
      }

      // 날짜 유효성 체크 및 기본값 설정 (필요한 경우에만)
      if (!projectData.startDate || !/^\d{4}-\d{2}-\d{2}$/.test(projectData.startDate) ||
          !projectData.endDate || !/^\d{4}-\d{2}-\d{2}$/.test(projectData.endDate) ||
          !projectData.deadline || !/^\d{4}-\d{2}-\d{2}$/.test(projectData.deadline)) {
        console.log('⚠️ 유효하지 않은 날짜 감지, 기본값 설정 실행');
        setDefaultDates();
      }
      
      // 로컬 스토리지에서 JWT 토큰 가져오기
      const token = localStorage.getItem('token');
      if (!token) {
        errorMessage = "로그인이 필요합니다.";
        isProcessing = false;
        return;
      }
      
      // 서버 API로 프로젝트 수정 요청
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(projectData)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log("프로젝트 수정 성공:", data);
        isUpdated = true;
        
        // 수정 후 정리
        localStorage.removeItem('editingProject');
        
        // 수정 성공 후 잠시 대기 후 프로젝트 관리 페이지로 이동
        setTimeout(() => {
          // 프로젝트 관리 탭으로 이동하도록 localStorage에 상태 저장
          localStorage.setItem('projectRegisterActiveTab', 'manage');
          setPage('project-register');
        }, 2000);
      } else {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || "프로젝트 수정에 실패했습니다.";
        console.error("프로젝트 수정 실패:", errorData);
      }
    } catch (error) {
      console.error("수정 오류:", error);
      errorMessage = `수정 오류: ${error.message}`;
    } finally {
      isProcessing = false;
    }
  }
  

  // 프로젝트 삭제 함수
  async function deleteProject() {
    if (!confirm("정말 이 프로젝트를 삭제하시겠습니까? 이 작업은 취소할 수 없습니다.")) {
      return;
    }
    
    try {
      isProcessing = true;
      errorMessage = "";
      
      // 로컬 스토리지에서 JWT 토큰 가져오기
      const token = localStorage.getItem('token');
      if (!token) {
        errorMessage = "로그인이 필요합니다.";
        isProcessing = false;
        return;
      }
      
      // 서버 API로 프로젝트 삭제 요청
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectData.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        console.log("프로젝트 삭제 성공");
        alert("프로젝트가 성공적으로 삭제되었습니다.");
        
        // 삭제 후 정리
        localStorage.removeItem('editingProject');
        
        // 삭제 후 프로젝트 관리 페이지로 이동
        localStorage.setItem('projectRegisterActiveTab', 'manage');
        setPage('project-register');
      } else {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || "프로젝트 삭제에 실패했습니다.";
        console.error("프로젝트 삭제 실패:", errorData);
      }
    } catch (error) {
      console.error("삭제 오류:", error);
      errorMessage = `삭제 오류: ${error.message}`;
    } finally {
      isProcessing = false;
    }
  }
  
  // 배열 항목 추가 함수
  function addArrayItem(arrayName) {
    projectData[arrayName] = [...projectData[arrayName], ""];
  }
  
  // 배열 항목 제거 함수
  function removeArrayItem(arrayName, index) {
    projectData[arrayName] = projectData[arrayName].filter((_, i) => i !== index);
  }
</script>

<div class="project-edit-container">
  <Navigation 
    {user}
    currentPage="project-edit"
  />
  
  <div class="project-edit-box">
    <div class="edit-title">
      <h2>공고 수정</h2>
    </div>
    
    {#if isUpdated}
      <div class="success-message">
        <div class="success-icon">✓</div>
        <h3>프로젝트가 성공적으로 수정되었습니다!</h3>
      </div>
    {:else}
      <div class="edit-form">
        <h2>프로젝트 정보 수정</h2>
        <p class="guidance">아래 내용을 수정한 후 저장해주세요.</p>
        
        <div class="form-grid">
          <!-- 제목 -->
          <div class="form-group">
            <label for="project-title">프로젝트 제목</label>
            <input type="text" id="project-title" bind:value={projectData.title}>
          </div>
          
          <!-- 회사명 -->
          <div class="form-group">
            <label for="company">회사명</label>
            <input type="text" id="company" bind:value={projectData.company}>
          </div>
          
          <!-- 프로젝트 기간 -->
          
          
          <!-- 개발자 등급/경력 -->
          <div class="form-group">
            <label for="level">개발자 등급/경력</label>
            <input type="text" id="level" bind:value={projectData.level}>
          </div>
          
          <!-- 인원 수 -->
          <div class="form-group">
            <label for="headcount">인원 수</label>
            <input type="text" id="headcount" bind:value={projectData.headcount}>
          </div>
          
          <!-- 근무 위치 -->
          <div class="form-group">
            <label for="region">근무 지역 </label>
            <div class="region-selection">
              <!-- 시/도 선택 -->
              <div class="region-row">
                <select id="city" bind:value={selectedCity} on:change={handleCityChange} disabled={isLoadingCities}>
                  <option value="">시/도 선택</option>
                  {#each cities as city}
                    <option value={city.code_value}>{city.code_name}</option>
                  {/each}
                </select>
                
              </div>
            </div>
          </div>

          <!-- 근무 위치 (주소 입력) -->
          <div class="form-group">
            <label for="location" class="form-label">근무 위치</label>
            <input 
              type="text" 
              id="location" 
              class="form-input" 
              bind:value={projectData.location}
              placeholder="예: 서울시 강남구 테헤란로 123, 삼성동 코엑스몰 근처"
              maxlength="200"
            >
            <small class="form-help-text">구체적인 근무 주소나 위치를 입력해주세요. (최대 200자)</small>
          </div>
          
          <!-- 급여 정보 -->
          <div class="form-group">
            <label for="salary">급여 정보</label>
            <input type="text" id="salary" bind:value={projectData.salary}>
          </div>
          
                      <!-- 시작일 -->
            <div class="form-group">
              <label for="startDate">시작일</label>
              <input type="date" id="startDate" bind:value={projectData.startDate} on:change={handleStartDateChange}>
            </div>
            
            <!-- 종료일 -->
            <div class="form-group">
              <label for="endDate">종료일</label>
              <input type="date" id="endDate" bind:value={projectData.endDate}>
            </div>
          
          <!-- 카테고리 -->
          <div class="form-group">
            <label for="category">카테고리</label>
            <select id="category" bind:value={projectData.category}>
              <option value="">선택하세요</option>
              {#each categories as category}
                <option value={category}>{category}</option>
              {/each}
            </select>
          </div>

          <!-- 공고 마감일 -->
          <div class="form-group">
            <label for="deadline">공고 마감일</label>
            <input 
              type="date" 
              id="deadline" 
              bind:value={projectData.deadline}
              min={getTodayString()}
              required
            >
            <small class="form-help-text">
              공고 마감일을 설정해주세요. 설정하지 않으면 자동으로 오늘로부터 5일 뒤로 설정됩니다.
            </small>
          </div>

          <!-- 공고 상태 -->
          <div class="form-group">
            <label for="status">공고 상태</label>
            <select id="status" bind:value={projectData.status}>
              {#each statusOptions as statusOption}
                <option value={statusOption.value}>{statusOption.label}</option>
              {/each}
            </select>
            <small class="form-help-text">
              {#each statusOptions as statusOption}
                {#if statusOption.value === projectData.status}
                  {statusOption.description}
                {/if}
              {/each}
            </small>
          </div>
        </div>
        
        <!-- 기술 스택 -->
        <div class="form-group">
          <label for="skills">필요 기술 스택</label>
          <div class="array-items">
            {#each projectData.skills as skill, index}
              <div class="array-item">
                <input type="text" bind:value={projectData.skills[index]}>
                <Button variant="danger" size="sm" on:click={() => removeArrayItem('skills', index)}>×</Button>
              </div>
            {/each}
            <Button variant="outline-primary" size="sm" on:click={() => addArrayItem('skills')}>+ 항목 추가</Button>
          </div>
        </div>
        
        <!-- 자격 요건 -->
        <div class="form-group">
          <label for="requirements">자격 요건</label>
          <div class="array-items">
            {#each projectData.requirements as req, index}
              <div class="array-item">
                <input type="text" bind:value={projectData.requirements[index]}>
                <Button variant="danger" size="sm" on:click={() => removeArrayItem('requirements', index)}>×</Button>
              </div>
            {/each}
            <Button variant="outline-primary" size="sm" on:click={() => addArrayItem('requirements')}>+ 항목 추가</Button>
          </div>
        </div>
        
        <!-- 우대 사항 -->
        <div class="form-group">
          <label for="preferredSkills">우대 사항</label>
          <div class="array-items">
            {#each projectData.preferredSkills as pref, index}
              <div class="array-item">
                <input type="text" bind:value={projectData.preferredSkills[index]}>
                <Button variant="danger" size="sm" on:click={() => removeArrayItem('preferredSkills', index)}>×</Button>
              </div>
            {/each}
            <Button variant="outline-primary" size="sm" on:click={() => addArrayItem('preferredSkills')}>+ 항목 추가</Button>
          </div>
        </div>
        
        <!-- 프로젝트 설명 -->
        <div class="form-group">
          <label for="description">프로젝트 상세 설명</label>
          <textarea id="description" bind:value={projectData.description} rows="5"></textarea>
        </div>
        
        <!-- 연락처 정보 -->
        <div class="form-group">
          <label for="contact">연락처 정보</label>
          <textarea id="contact" bind:value={projectData.contact} rows="3"></textarea>
        </div>

        <!-- 관리자 전용 비고 (관리자만 표시) -->
        {#if isAdmin}
          <div class="form-group admin-only-field">
            <label for="admin-notes">비고</label>
            <textarea 
              id="admin-notes" 
              bind:value={projectData.admin_notes} 
              rows="3"
              placeholder="비고사항을 입력하세요."
            ></textarea>
            
            <hr class="form-divider">
            
            <label for="admin-remark">비고 2</label>
            <textarea 
              id="admin-remark" 
              bind:value={projectData.admin_remark} 
              rows="3"
              placeholder="비고사항을 입력하세요."
            ></textarea>
            <small class="form-help-text">
              이 내용은 관리자만 볼 수 있으며, 일반 사용자에게는 표시되지 않습니다.
            </small>
          </div>
        {/if}
        
        {#if errorMessage}
          <div class="error-message">{errorMessage}</div>
        {/if}
        
        <ButtonGroup attached={false} responsive={true}>
          <Button variant="danger" on:click={deleteProject}>
            프로젝트 삭제
          </Button>
          <Button 
            variant="secondary" 
            on:click={() => {
              // 취소 시 프로젝트 관리 탭으로 이동
              localStorage.setItem('projectRegisterActiveTab', 'manage');
              setPage('project-register');
            }}
          >
            취소
          </Button>
          <Button 
            variant="primary" 
            on:click={updateProject}
            disabled={isProcessing || !projectData.title || !projectData.category || !projectData.status || !projectData.deadline}
            loading={isProcessing}
          >
            {isProcessing ? "업데이트 중..." : "프로젝트 수정 저장"}
          </Button>
        </ButtonGroup>
      </div>
    {/if}
  </div>
</div>

<style>
  .project-edit-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: #f9f9f9;
    font-family: 'Noto Sans KR', sans-serif;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
  
  .project-edit-box {
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    padding: 40px;
    width: 100%;
    max-width: 900px;
    margin: 40px auto;
  }
  
  .edit-title {
    text-align: center;
    margin-bottom: 30px;
  }
  
  .edit-title h2 {
    color: #333;
    font-size: 1.8rem;
    font-weight: 700;
  }
  
  .edit-form {
    margin-bottom: 30px;
  }

  .form-help-text {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: #6c757d;
    font-style: italic;
  }

  .admin-only-field {
    border: 2px solid #007bff;
    border-radius: 8px;
    padding: 1rem;
    background-color: #f8f9ff;
    position: relative;
    margin-top: 1.5rem;
  }

  .admin-only-field::before {
    content: "👑 관리자 전용";
    position: absolute;
    top: -12px;
    left: 12px;
    background-color: #007bff;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .admin-only-field label {
    color: #007bff;
    font-weight: 600;
  }

  .admin-only-field .form-help-text {
    color: #007bff;
    font-weight: 500;
  }
  
  .edit-form h2 {
    font-size: 1.5rem;
    margin-bottom: 10px;
    color: #333;
  }
  
  .guidance {
    color: #666;
    margin-bottom: 20px;
    font-size: 0.95rem;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 768px) {
    .project-edit-container {
      padding: 0 10px;
    }
    
    .project-edit-box {
      margin: 20px auto;
      padding: 20px;
    }
    
    .form-grid {
      grid-template-columns: 1fr;
    }
  }
  
  label {
    display: block;
    font-size: 0.95rem;
    color: #555;
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 1rem;
    transition: border-color 0.3s;
  }
  
  input:focus, textarea:focus, select:focus {
    border-color: #3273dc;
    outline: none;
  }
  
  .array-items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .array-item {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  

  
  .error-message {
    padding: 12px;
    border-radius: 5px;
    margin: 15px 0;
    background-color: #ffeeee;
    color: #cc0000;
    font-size: 0.95rem;
  }
  
  .success-message {
    text-align: center;
    padding: 40px 0;
  }
  
  .success-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background-color: #3273dc;
    color: white;
    font-size: 2.5rem;
    border-radius: 50%;
    margin-bottom: 20px;
  }
  
  .success-message h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 15px;
  }
</style> 